import React, { useState, useEffect } from 'react';
import { MapPin, Instagram, ExternalLink, Mic2, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { logError } from '../lib/logError';

const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function CypherItaliaPage() {
  const [cyphers, setCyphers] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dayFilter, setDayFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/site-content/cyphers`)
      .then(r => r.json())
      .then(d => {
        setCyphers(Array.isArray(d.items) ? d.items : []);
        setUpdatedAt(d.updated_at);
      })
      .catch(e => logError('[cypher] caricamento fallito', e))
      .finally(() => setLoading(false));
  }, []);

  const days = DAY_ORDER
    .map(d => ({ key: d, ...((cyphers.find(c => c.day === d)) || {}) }))
    .filter(d => cyphers.some(c => c.day === d.key));

  const q = search.trim().toLowerCase();
  const filtered = cyphers.filter(c => {
    if (dayFilter && c.day !== dayFilter) return false;
    if (q && !`${c.name} ${c.city}`.toLowerCase().includes(q)) return false;
    return true;
  });

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: 'var(--tp-black)' }}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            CYPHER <span className="text-red-600">D'ITALIA</span>
          </h1>
          <p className="text-gray-400 text-base max-w-2xl">
            La mappa delle cypher freestyle attive in tutta Italia: trova il cerchio più vicino a te,
            scopri il giorno in cui si rappa e unisciti alla community.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-gray-500">
            <span data-testid="cypher-count" className="px-2.5 py-1 rounded-full bg-red-900/40 text-red-300 border border-red-800 font-bold">
              {cyphers.length} cypher attive
            </span>
            {updatedAt && (
              <span data-testid="cypher-updated">
                Aggiornato il {new Date(updatedAt).toLocaleDateString('it-IT')} · aggiornamento automatico il 30 di ogni mese
              </span>
            )}
            <a
              href="https://rapscript.github.io/cypher-map/italy"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cypher-source-link"
              className="inline-flex items-center gap-1 text-gray-500 hover:text-red-400 transition-colors underline"
            >
              Fonte: cypher-map <ExternalLink size={11} />
            </a>
          </div>
        </div>

        {/* Filtri */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              data-testid="cypher-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Cerca cypher o città..."
              className="bg-gray-900 border-gray-700 text-white pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="cypher-day-all"
              onClick={() => setDayFilter('')}
              className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${
                !dayFilter ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              style={{ fontFamily: 'Oswald, sans-serif' }}>
              TUTTI
            </button>
            {days.map(d => (
              <button
                key={d.key}
                data-testid={`cypher-day-${d.key}`}
                onClick={() => setDayFilter(dayFilter === d.key ? '' : d.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors flex items-center gap-1.5 ${
                  dayFilter === d.key ? 'text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
                style={{
                  fontFamily: 'Oswald, sans-serif',
                  backgroundColor: dayFilter === d.key ? d.color : undefined,
                }}>
                <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                {(d.day_it || d.key).toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Griglia cypher */}
        {loading ? (
          <div className="text-center py-20 text-gray-500">Caricamento cypher...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 rounded-lg border border-gray-800" style={{ backgroundColor: 'var(--tp-dark)' }}>
            <Mic2 size={36} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">
              {cyphers.length === 0 ? 'Dati non disponibili al momento, riprova più tardi.' : 'Nessuna cypher corrisponde ai filtri.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c) => (
              <a
                key={`${c.name}-${c.city}`}
                href={c.link || undefined}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`cypher-card-${c.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className="group relative rounded-lg border-2 border-gray-800 hover:border-red-600 p-4 transition-all duration-300 hover:-translate-y-1 block"
                style={{ backgroundColor: 'var(--tp-dark)' }}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-white text-lg font-bold leading-tight group-hover:text-red-400 transition-colors" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    {c.name}
                  </h3>
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white whitespace-nowrap flex-shrink-0"
                    style={{ backgroundColor: c.color, fontFamily: 'Oswald, sans-serif' }}
                  >
                    {(c.day_it || c.day).toUpperCase()}
                  </span>
                </div>
                <p className="text-gray-400 text-sm flex items-center gap-1.5">
                  <MapPin size={13} className="text-red-500 flex-shrink-0" />
                  {c.city}
                </p>
                {c.link && (
                  <p className="text-gray-500 text-xs mt-3 flex items-center gap-1.5 group-hover:text-pink-400 transition-colors">
                    <Instagram size={13} />
                    {c.link.replace(/https?:\/\/(www\.)?instagram\.com\//, '@').replace(/\/$/, '')}
                  </p>
                )}
              </a>
            ))}
          </div>
        )}

        {/* Nota finale */}
        <div className="mt-12 p-5 rounded-lg border border-gray-800 text-center" style={{ backgroundColor: 'var(--tp-dark)' }}>
          <p className="text-gray-400 text-sm">
            Organizzi una cypher e vuoi farla conoscere? Scrivici dalla pagina{' '}
            <a href="/contatti" className="text-red-400 hover:text-red-300 underline">Contatti</a> e spargi la voce!
          </p>
        </div>
      </div>
    </div>
  );
}
